// Utilitaires pour la gestion des liens YouTube

// Patterns pour détecter les URLs YouTube
export const YOUTUBE_URL_PATTERNS = [
  /https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/watch\?v=([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(www\.)?(youtube\.com)\/embed\/([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(www\.)?(youtube\.com)\/v\/([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(m\.)?(youtube\.com)\/watch\?v=([a-zA-Z0-9_-]{11})/g
];

/**
 * Détecte si une URL est un lien YouTube
 */
export const isYouTubeUrl = (url: string): boolean => {
  return YOUTUBE_URL_PATTERNS.some(pattern => {
    pattern.lastIndex = 0;
    return pattern.test(url);
  });
};

/**
 * Détecte tous les liens YouTube dans un texte
 */
export const detectYouTubeLinks = (text: string): string[] => {
  const links: string[] = [];
  
  YOUTUBE_URL_PATTERNS.forEach(pattern => {
    pattern.lastIndex = 0;
    const matches = text.match(pattern);
    if (matches) {
      links.push(...matches);
    }
  });
  
  // Supprimer les doublons basés sur l'ID de la vidéo
  const uniqueLinks = new Map<string, string>();
  
  links.forEach(link => {
    const videoId = extractYouTubeVideoId(link);
    if (videoId && !uniqueLinks.has(videoId)) {
      uniqueLinks.set(videoId, link);
    }
  });
  
  return Array.from(uniqueLinks.values());
};

/**
 * Extrait l'ID de la vidéo YouTube à partir d'une URL
 */
export const extractYouTubeVideoId = (url: string): string | null => {
  // Pattern pour youtube.com/watch?v=
  let match = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  // Pattern pour youtu.be/
  match = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  // Pattern pour youtube.com/embed/
  match = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  // Pattern pour youtube.com/v/
  match = url.match(/youtube\.com\/v\/([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  return null;
};

/**
 * Génère l'URL d'embed YouTube à partir d'une URL YouTube
 */
export const getYouTubeEmbedUrl = (url: string): string | null => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`;
};

/**
 * Génère l'URL de la miniature YouTube
 */
export const getYouTubeThumbnailUrl = (url: string, quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'medium'): string | null => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  
  const qualityMap = {
    'default': 'default',
    'medium': 'mqdefault',
    'high': 'hqdefault',
    'standard': 'sddefault',
    'maxres': 'maxresdefault'
  };
  
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

/**
 * Extrait le timestamp d'une URL YouTube (paramètre t)
 */
export const extractYouTubeTimestamp = (url: string): number | null => {
  const match = url.match(/[?&]t=(\d+)/);
  return match ? parseInt(match[1]) : null;
};

/**
 * Génère l'URL d'embed avec timestamp
 */
export const getYouTubeEmbedUrlWithTimestamp = (url: string): string | null => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  
  const timestamp = extractYouTubeTimestamp(url);
  const baseUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`;
  
  return timestamp ? `${baseUrl}&start=${timestamp}` : baseUrl;
};
